import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { TStoreItem, TStoreCategory } from '@smartico/public-api/dist/WSAPI/WSAPITypes'
import { Smartico } from '../../core/Smartico'

interface StoreState {
    items: TStoreItem[]
    categories: TStoreCategory[] | null
    error: string | null
}

const initialState: StoreState = {
    items: [],
    categories: null,
    error: null,
}

export const fetchStoreItems = createAsyncThunk<TStoreItem[], void>('store/fetchStoreItems', async (_, { rejectWithValue }) => {
	try {
		const smarticoApi = await Smartico.apiReady
		const response = await smarticoApi.getStoreItems()
		return response
	} catch (error) {
		return rejectWithValue(error)
	}
})

export const fetchStoreCategories = createAsyncThunk<TStoreCategory[], void>('store/fetchStoreCategories', async (_, { rejectWithValue }) => {
	try {
		const smarticoApi = await Smartico.apiReady
		const response = await smarticoApi.getStoreCategories()
		return response
	} catch (error) {
		return rejectWithValue(error)
	}
})

const storeSlice = createSlice({
    name: 'store',
    initialState,
    reducers: {
        setStoreItems(state, action: PayloadAction<TStoreItem[]>) {
            state.items = action.payload
        },
        setStoreCategories(state, action: PayloadAction<TStoreCategory[]>) {
            state.categories = action.payload
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStoreItems.pending, (state) => {
                state.items = []
                state.error = null
            })
            .addCase(fetchStoreItems.fulfilled, (state, action) => {
                state.items = action.payload
                state.error = null
            })
            .addCase(fetchStoreItems.rejected, (state, action) => {
                state.items = []
                state.error = action.payload as string
            })
        builder
            .addCase(fetchStoreCategories.pending, (state) => {
                state.categories = null
                state.error = null
            })
            .addCase(fetchStoreCategories.fulfilled, (state, action) => {
                state.categories = action.payload
                state.error = null
            })
            .addCase(fetchStoreCategories.rejected, (state, action) => {
                state.categories = null
                state.error = action.payload as string
            })
    },
})

export const { setStoreItems, setStoreCategories } = storeSlice.actions

export default storeSlice.reducer